import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/AuthProvider";
import { WorkspaceProvider } from "@/hooks/WorkspaceProvider";
import { TierOverrideProvider } from "@/hooks/TierOverrideProvider";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AppShell } from "@/components/AppShell";
import Auth from "./pages/Auth";
import AuthFinalize from "./pages/AuthFinalize";
import OAuthConsent from "./pages/OAuthConsent";
import { PendingOAuthResumer } from "./components/PendingOAuthResumer";
import ResetPassword from "./pages/ResetPassword";
import Landing from "./pages/Landing";

import Pricing from "./pages/Pricing";
import Excerpts from "./pages/Excerpts";
import Dashboard from "./pages/Dashboard";
import ExcerptDetail from "./pages/ExcerptDetail";
import ExcerptEditor from "./pages/ExcerptEditor";
import MyLibrary from "./pages/MyLibrary";

import ImageStudio from "./pages/ImageStudio";
import Notes from "./pages/Notes";
import Help from "./pages/Help";
import HelpArticle from "./pages/HelpArticle";
import WorkspaceMedia from "./pages/WorkspaceMedia";
import Settings from "./pages/Settings";
import SettingsCredits from "./pages/SettingsCredits";

import Admin from "./pages/Admin";
import SuperAdmin from "./pages/SuperAdmin";
import AdminAnnouncements from "./pages/AdminAnnouncements";
import AdminComposerTemplates from "./pages/AdminComposerTemplates";
import AdminComposerPrompts from "./pages/AdminComposerPrompts";
import AdminEmailPreview from "./pages/AdminEmailPreview";
import AdminChangelog from "./pages/AdminChangelog";
import NotFound from "./pages/NotFound";
import { AdminGate } from "./components/AdminGate";
import GalleryPreview from "./pages/GalleryPreview";
import ReadwiseHighlights from "./pages/ReadwiseHighlights";
import MyFeedReader from "./pages/MyFeedReader";
import MyPodcasts from "./pages/MyPodcasts";
import CheckoutReturn from "./pages/CheckoutReturn";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Cookies from "./pages/Cookies";
import LegalCitationStyles from "./pages/LegalCitationStyles";
import FeaturePage from "./pages/FeaturePage";
import PublicProfile from "./pages/PublicProfile";
import Composer from "./pages/Composer";
import Publisher from "./pages/Publisher";
import ComposerDispatches from "./pages/ComposerDispatches";
import ComposerInbox from "./pages/ComposerInbox";
import Unsubscribe from "./pages/Unsubscribe";
import DowngradeReview from "./pages/DowngradeReview";
import InviteAccept from "./pages/InviteAccept";
import UsernameOnboarding from "./pages/UsernameOnboarding";
import Affiliate from "./pages/Affiliate";
import { ReferralCapture } from "./components/ReferralCapture";
import { PaymentTestModeBanner } from "./components/PaymentTestModeBanner";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { MarketingLayout } from "./components/marketing/MarketingLayout";
import { CookieConsentBanner } from "./components/CookieConsentBanner";
import { useAuth } from "./hooks/useAuth";
import { ScrollToTop } from "./components/ScrollToTop";
import { setSentryUser } from "./lib/sentry";
import { usePlausible } from "./hooks/usePlausible";
import { useEffect, useState } from "react";

const queryClient = new QueryClient();

const AnalyticsBoot = () => {
  const { user } = useAuth();
  usePlausible();
  useEffect(() => {
    setSentryUser(user?.id ?? null);
  }, [user?.id]);
  return null;
};

/**
 * Defers Sentry boot by ~1s so it doesn't compete with hero rendering on
 * first paint. Sentry's global handler still catches errors before this
 * point. Plausible will mount via the same gate in Phase 3.
 */
const DeferredAnalytics = () => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    const boot = () => setReady(true);
    if (typeof w.requestIdleCallback === "function") {
      const id = w.requestIdleCallback(boot, { timeout: 2000 });
      return () => w.cancelIdleCallback?.(id);
    }
    const t = setTimeout(boot, 1000);
    return () => clearTimeout(t);
  }, []);
  return ready ? <AnalyticsBoot /> : null;
};


/**
 * Rewrites the address bar to the trailing-slash form on first mount so
 * `/pricing` becomes `/pricing/` without a real 301. The canonical <link>
 * is the load-bearing SEO signal; this just keeps the URL consistent for
 * users and analytics. Skips root, already-slashed paths, and file-like
 * paths (anything with a dot in the last segment).
 */
const TrailingSlashNormalizer = () => {
  const { pathname, search, hash } = useLocation();
  useEffect(() => {
    // SEO consolidation only matters on the canonical production host.
    // Running this inside the Lovable preview iframe causes the wrapper
    // to re-inject its token/overlay on every nav, producing a double-load
    // and swallowing top-nav clicks.
    const host = window.location.hostname;
    const isProdHost = host === "excerptus.io" || host === "www.excerptus.io";
    if (!isProdHost) return;
    if (pathname === "/" || pathname.endsWith("/")) return;
    const lastSegment = pathname.split("/").pop() ?? "";
    if (lastSegment.includes(".")) return;
    window.history.replaceState(null, "", `${pathname}/${search}${hash}`);
  }, [pathname, search, hash]);
  return null;
};


export const AppRoutes = () => (
  <Routes>
    <Route element={<MarketingLayout />}>
      <Route path="/" element={<Landing />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/auth/finalize" element={<AuthFinalize />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/checkout/return" element={<CheckoutReturn />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/cookies" element={<Cookies />} />
      <Route path="/legal/citation-styles" element={<LegalCitationStyles />} />
      <Route path="/help" element={<Help />} />
      <Route path="/help/:slug" element={<HelpArticle />} />
      <Route path="/features/:slug" element={<FeaturePage />} />
      <Route path="/unsubscribe" element={<Unsubscribe />} />

    </Route>
    <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
    <Route path="/_gallery-preview" element={<GalleryPreview />} />
    
    <Route path="/invite/:token" element={<InviteAccept />} />
    <Route path="/u/:username" element={<PublicProfile />} />
    <Route
      path="/onboarding/username"
      element={<ProtectedRoute><UsernameOnboarding /></ProtectedRoute>}
    />
    <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/excerpts" element={<Excerpts />} />
      <Route path="/my-library" element={<MyLibrary />} />
      <Route path="/readwise-highlights" element={<ReadwiseHighlights />} />
      <Route path="/feed-reader" element={<MyFeedReader />} />
      <Route path="/podcasts" element={<MyPodcasts />} />
      <Route path="/new-excerpt" element={<ExcerptEditor />} />
      <Route path="/excerpts/:id" element={<ExcerptDetail />} />
      <Route path="/excerpts/:id/edit" element={<ExcerptEditor />} />
      
      <Route path="/composer" element={<Composer />} />
      <Route path="/publisher" element={<Publisher />} />
      <Route path="/composer/dispatches" element={<ComposerDispatches />} />
      <Route path="/composer/inbox" element={<ComposerInbox />} />
      <Route path="/image-studio" element={<ImageStudio />} />
      <Route path="/notes" element={<Notes />} />
      <Route path="/workspace-media" element={<WorkspaceMedia />} />

      <Route path="/settings" element={<Settings />} />
      <Route path="/settings/credits" element={<SettingsCredits />} />
      <Route path="/affiliate" element={<Affiliate />} />
      <Route path="/downgrade-review" element={<DowngradeReview />} />
      <Route path="/admin" element={<AdminGate><Admin /></AdminGate>} />
      <Route path="/super-admin" element={<AdminGate requireSuperAdmin><SuperAdmin /></AdminGate>} />
      <Route path="/admin/announcements" element={<AdminGate><AdminAnnouncements /></AdminGate>} />
      <Route path="/admin/composer-templates" element={<AdminGate><AdminComposerTemplates /></AdminGate>} />
      <Route path="/admin/composer-prompts" element={<AdminGate requireSuperAdmin><AdminComposerPrompts /></AdminGate>} />
      <Route path="/admin/emails/preview" element={<AdminGate><AdminEmailPreview /></AdminGate>} />
      <Route path="/admin/changelog" element={<AdminGate><AdminChangelog /></AdminGate>} />

    </Route>
    <Route path="*" element={<MarketingLayout />}>
      <Route path="*" element={<NotFound />} />
    </Route>
  </Routes>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Sonner />
      <BrowserRouter>
        <TrailingSlashNormalizer />
        <ScrollToTop />
        <ReferralCapture />
        <AuthProvider>
          <DeferredAnalytics />
          <PendingOAuthResumer />

          <TierOverrideProvider>
            <WorkspaceProvider>
              <div className="flex h-screen w-full flex-col overflow-hidden">
                <PaymentTestModeBanner />
                <div id="app-scroll-root" className="flex-1 min-h-0 overflow-y-auto">
                  <ErrorBoundary>
                    <AppRoutes />
                  </ErrorBoundary>
                </div>
                <CookieConsentBanner />
              </div>
            </WorkspaceProvider>
          </TierOverrideProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;

